import type { UndoAction } from '@/lib/history/types/undo-action'
import type { ShapeListOp } from '@/lib/shapes/types/shape-list-op'
import { withInsertedShapeAtIndex } from '@/lib/shapes/shape-list-ops'
import type { HistoryDirection } from '@/lib/history/types/history-direction'
import type { HistoryPersistencePlan } from '@/lib/history/types/history-persistence-plan'
import type { HistoryApplyRuntime } from './types/history-apply-runtime'
import type { ChromeSnapshot } from './types/chrome-snapshot'
import type { HistoryApplyOutcome } from './types/history-apply-outcome'
import {
  createEmptyPersistencePlan,
  mergePersistencePlans,
  pushUnique,
} from './helpers/persistence-plan'
import { orderedCompoundReplayActions, shapeListOpForAction } from './helpers/compound-replay'
import {
  getSelectionChromeForDirection,
  selectShapesForHistoryReplay,
  selectionAfterHistoryStep,
} from './helpers/history-selection'
import { shapeUpdateOperation } from './helpers/shape-update-operation'

export function applyHistoryAction(
  action: UndoAction,
  direction: HistoryDirection,
  runtime: HistoryApplyRuntime,
  opts?: { skipShapeListMutation?: boolean }
): HistoryApplyOutcome {
  if (action.type === 'compound') {
    const replay = orderedCompoundReplayActions(action, direction)
    const hasSelectionReplay = replay.some((subAction) => subAction.type === 'selection')
    let chromeToRestore: ChromeSnapshot | null = null
    let shouldResyncMultiSelectChrome = false
    let merged: HistoryPersistencePlan = createEmptyPersistencePlan(action.type, direction)
    // Ids touched by anything other than a chain-reflow side effect — see `reflowOnly`.
    const editedIds = new Set<string>()

    // Batch all add/remove shape-list mutations implied by this transaction into a single
    // manager call, in the already-correctly-ordered `replay` sequence, instead of letting each
    // sub-action below call `loadShapes`/`removeShape` individually. React 18 batches N
    // sequential `loadShapes({clearExisting:true})` calls made in the same tick, and since each
    // one computes its inserted array from a stale pre-loop snapshot, only the last would survive
    // (WMPCM-5019/7165).
    if (runtime.shapeManager) {
      const ops = replay
        .map((subAction) => shapeListOpForAction(subAction, direction))
        .filter((op): op is ShapeListOp => op !== null)

      if (ops.length > 0) runtime.shapeManager.applyShapeListOps(ops)
    }

    for (const subAction of replay) {
      const outcome = applyHistoryAction(subAction, direction, runtime, {
        skipShapeListMutation: true,
      })
      const {
        chromeToRestore: nextChromeToRestore,
        shouldResyncMultiSelectChrome: nextShouldResync,
      } = outcome

      if (nextChromeToRestore) chromeToRestore = nextChromeToRestore
      shouldResyncMultiSelectChrome = shouldResyncMultiSelectChrome || nextShouldResync
      merged = mergePersistencePlans(merged, outcome.persistence)
      if (!(subAction.type === 'update' && subAction.reflowOnly)) {
        for (const id of outcome.persistence.batchElementIds ?? []) editedIds.add(id)
      }
    }

    if (!hasSelectionReplay) {
      const touchedIds = merged.batchElementIds ?? []

      // batchElementIds mixes shape and text ids indiscriminately (markTouched above doesn't
      // distinguish) — a compound with no explicit 'selection' sub-action (e.g. a multi-object
      // delete/cut, which only carries 'remove'/'textRemove' atomics) needs both halves restored
      // on undo, or text members silently drop out of the post-undo selection.
      if (touchedIds.length > 0 && runtime.shapeManager) {
        const validShapeIds = new Set(runtime.shapeManager.getShapes().map((shape) => shape.id))
        const validTouchedIds = touchedIds.filter((id) => validShapeIds.has(id))
        // Frames a chain reflow only re-flowed are not part of what the user edited, so undoing a
        // wrap source's rotate keeps just that shape selected (WMPCM-10558). A step made only of
        // reflow keeps selecting every frame it touched, as before.
        const validEditedIds = validTouchedIds.filter((id) => editedIds.has(id))
        const nextSelection = validEditedIds.length > 0 ? validEditedIds : validTouchedIds

        if (nextSelection.length > 0) {
          selectShapesForHistoryReplay(runtime.shapeManager, nextSelection)
          merged.syncActiveFromShapeSelection = true
        }
      }
    }

    return {
      chromeToRestore,
      shouldResyncMultiSelectChrome,
      persistence: merged,
    }
  }

  const { shapeManager: mgr } = runtime
  const persistence: HistoryPersistencePlan = createEmptyPersistencePlan(action.type, direction)
  const touchedElementIds: string[] = []
  const markTouched = (id: string) => {
    if (!touchedElementIds.includes(id)) touchedElementIds.push(id)
  }

  if (action.type === 'add') {
    if (!opts?.skipShapeListMutation) {
      if (direction === 'undo') {
        mgr?.removeShape(action.shape.id)
      } else if (mgr) {
        mgr.loadShapes(
          withInsertedShapeAtIndex(mgr.getShapes(), action.shape, action.insertionIndex),
          {
            clearExisting: true,
          }
        )
      }
    }

    pushUnique(persistence.shapeMarkEditedIds, action.shape.id)
    markTouched(action.shape.id)
    if (direction === 'undo') {
      persistence.shapeDeleteIds.push(action.shape.id)
    } else {
      persistence.shapeUpserts.push({
        shapeId: action.shape.id,
        operation: 'update',
        mode: 'add',
        shape: action.shape,
      })
    }
  } else if (action.type === 'remove') {
    if (!opts?.skipShapeListMutation) {
      if (direction === 'undo') {
        if (mgr) {
          mgr.loadShapes(
            withInsertedShapeAtIndex(mgr.getShapes(), action.shape, action.insertionIndex),
            {
              clearExisting: true,
            }
          )
        }
      } else {
        mgr?.removeShape(action.shape.id)
      }
    }

    pushUnique(persistence.shapeMarkEditedIds, action.shape.id)
    markTouched(action.shape.id)
    if (direction === 'undo') {
      persistence.shapeUpserts.push({
        shapeId: action.shape.id,
        operation: 'update',
        mode: 'add',
        shape: action.shape,
      })
    } else {
      persistence.shapeDeleteIds.push(action.shape.id)
    }
  } else if (action.type === 'batchRemove') {
    if (direction === 'undo') {
      if (mgr) {
        const restoredIds = new Set(action.shapes.map((s) => s.id))
        const tail = mgr.getShapes().filter((s) => !restoredIds.has(s.id))

        mgr.loadShapes([...action.shapes, ...tail], { clearExisting: true })
      }
    } else if (mgr) {
      for (const shape of action.shapes) {
        mgr.removeShape(shape.id)
      }
    }

    for (const shape of action.shapes) {
      pushUnique(persistence.shapeMarkEditedIds, shape.id)
      markTouched(shape.id)
      if (direction === 'undo') {
        persistence.shapeUpserts.push({
          shapeId: shape.id,
          operation: 'update',
          mode: 'add',
          shape,
        })
      } else {
        persistence.shapeDeleteIds.push(shape.id)
      }
    }
  } else if (action.type === 'update') {
    if (mgr) {
      const prevSelection = mgr.getSelectedShapeIds()
      const patch = direction === 'undo' ? action.before : action.after

      mgr.updateShape(action.shapeId, patch, { bypassLockGuard: true })
      // If this shape is the one currently being edited, its text-edit-session baseline (used by
      // `commitShapeTextNow` to diff "what changed since the last commit") otherwise keeps
      // describing the PRE-undo/redo state — the next commit would then see this reverted change
      // as new and re-apply it, undoing the undo one step later. See its own doc comment.
      mgr.resyncShapeTextEditBaseline(action.shapeId)
      const validIds = new Set(mgr.getShapes().map((s) => s.id))
      const next = selectionAfterHistoryStep(prevSelection, validIds, [action.shapeId])

      selectShapesForHistoryReplay(mgr, next)
      pushUnique(persistence.shapeMarkEditedIds, action.shapeId)
      markTouched(action.shapeId)
      persistence.shapeUpserts.push({
        shapeId: action.shapeId,
        operation: shapeUpdateOperation(action.before, action.after),
        mode: 'update',
      })
      persistence.syncActiveFromShapeSelection = true
    }
  } else if (action.type === 'batchUpdate') {
    if (mgr) {
      const prevSelection = mgr.getSelectedShapeIds()
      const touched: string[] = []

      // One state publish: multiple `updateShape` calls each schedule `setShapes`, and React 18
      // batching can collapse those so only the last updater wins — leaving group children wrong
      // after undo/redo (especially on the second+ cycle).
      mgr.applyShapeHistoryPatches(
        action.items.map((item) => ({
          shapeId: item.shapeId,
          patch: direction === 'undo' ? item.before : item.after,
        }))
      )

      for (const item of action.items) {
        touched.push(item.shapeId)
      }

      const validIds = new Set(mgr.getShapes().map((s) => s.id))
      const next = selectionAfterHistoryStep(prevSelection, validIds, Array.from(new Set(touched)))

      selectShapesForHistoryReplay(mgr, next)
      for (const item of action.items) {
        pushUnique(persistence.shapeMarkEditedIds, item.shapeId)
        markTouched(item.shapeId)
        persistence.shapeUpserts.push({
          shapeId: item.shapeId,
          operation: shapeUpdateOperation(item.before, item.after),
          mode: 'update',
        })
      }

      persistence.syncActiveFromShapeSelection = true
    }
  } else if (action.type === 'reorder') {
    const order = direction === 'undo' ? action.beforeOrder : action.afterOrder
    const fiToApply = direction === 'undo' ? action.beforeFIChanges : action.fiChanges

    mgr?.reorderShapes(order)

    if (mgr && fiToApply) {
      for (const [id, fi] of Object.entries(fiToApply)) {
        mgr.updateShape(id, { fractionalIndex: fi })
        pushUnique(persistence.shapeResequenceIds, id)
        markTouched(id)
      }
    }
  } else if (action.type === 'convertToPath') {
    if (mgr) {
      const targetShape = direction === 'undo' ? action.shape : action.convertedShape
      const removeId = direction === 'undo' ? action.convertedShape.id : action.shape.id
      const currentShapes = mgr.getShapes()
      const withoutRemoved = currentShapes.filter((s) => s.id !== removeId)

      mgr.loadShapes(withInsertedShapeAtIndex(withoutRemoved, targetShape, action.insertionIndex), {
        clearExisting: true,
      })
      selectShapesForHistoryReplay(mgr, [targetShape.id])
      pushUnique(persistence.shapeMarkEditedIds, targetShape.id)
      markTouched(targetShape.id)
      persistence.shapeTypeConversions = [{ deleteId: removeId, addShape: targetShape }]
      persistence.syncActiveFromShapeSelection = true
    }
  } else if (action.type === 'unifiedReorder') {
    const order = direction === 'undo' ? action.beforeOrder : action.afterOrder
    const fiToApply = direction === 'undo' ? action.beforeFIChanges : action.fiChanges

    const shapeIds = new Set(mgr?.getShapes().map((s) => s.id) ?? [])

    mgr?.reorderShapes(order.filter((id) => shapeIds.has(id)))

    if (fiToApply) {
      for (const [id, fi] of Object.entries(fiToApply)) {
        if (shapeIds.has(id)) {
          mgr?.updateShape(id, { fractionalIndex: fi })
          pushUnique(persistence.shapeResequenceIds, id)
        }

        markTouched(id)
      }
    }

    persistence.unifiedLayerOrder = order
  } else if (action.type === 'selection') {
    if (mgr) {
      const validShapeIds = new Set(mgr.getShapes().map((s) => s.id))
      const nextShapeIds =
        direction === 'undo'
          ? action.beforeIds.filter((id) => validShapeIds.has(id))
          : action.afterIds.filter((id) => validShapeIds.has(id))

      selectShapesForHistoryReplay(mgr, nextShapeIds)
    }
  } else if (action.type === 'textLinkChange') {
    persistence.linkedTextChainsUpsert = {
      chains: direction === 'undo' ? action.chainsBefore : action.chainsAfter,
      riderTextBoxId: action.riderTextBoxId,
    }
    // The rider is always a ShapeManager shape now (text-unification). Note that redoing a
    // whole-chain delete marks a rider that redo is about to remove — the prune rides on the dying
    // frame there, because it is the only element still live server-side (see
    // `repairChainAfterFrameDeleted`'s no-survivor branch). The lock is advisory and idle-released,
    // so that is harmless rather than something to special-case.
    pushUnique(persistence.shapeMarkEditedIds, action.riderTextBoxId)
    markTouched(action.riderTextBoxId)
  } else if (action.type === 'spreadBackgrounds') {
    // Spread-level state outside the ShapeManager: nothing to mutate here. The plan carries the
    // target slots, and home's background bridge restores and saves them.
    persistence.spreadBackgroundsRestore = {
      spreadId: action.spreadId,
      slots: direction === 'undo' ? action.before : action.after,
    }
  }

  const chromeToRestore = getSelectionChromeForDirection(action, direction)

  persistence.batchElementIds = touchedElementIds
  if (touchedElementIds.length > 0) {
    persistence.historyTxnId =
      action.historyTxnId ??
      `${action.type}:${direction}:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`
  }

  return {
    chromeToRestore,
    shouldResyncMultiSelectChrome: chromeToRestore === null,
    persistence,
  }
}
